/**
 * Microwave listing spec (Wave 2 appliance inverter Phase 1).
 *
 * Canonical id: `microwave` (new). Four legacy microwave ids are aliases.
 * Both `appliance_inverter_type` and `microwave_smart_type` required.
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  inverterTitleToken,
  smartTitleToken,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import {
  APPLIANCE_INVERTER_TYPES,
  CANONICAL_MICROWAVE_SUBCATEGORY_ID,
  getLegacySubcategoryInverterMapping,
  isApplianceInverterType,
  isMicrowaveSmartType,
  LEGACY_MICROWAVE_SUBCATEGORY_IDS,
  MICROWAVE_SMART_TYPES,
  pushApplianceInverterInvalidIssue,
  pushApplianceInverterRequiredIssue,
  resolveInverterTypeForLegacySubcategory,
  resolveMicrowaveSmartTypeForLegacySubcategory,
  type ApplianceInverterType,
  type MicrowaveSmartType,
} from "@/lib/listing/applianceInverterListingSpec";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const MICROWAVE_SUBCATEGORY_IDS = [
  CANONICAL_MICROWAVE_SUBCATEGORY_ID,
  ...LEGACY_MICROWAVE_SUBCATEGORY_IDS,
] as const;

export type MicrowaveSubcategoryId = (typeof MICROWAVE_SUBCATEGORY_IDS)[number];

export type MicrowaveSpecFormState = {
  applianceInverterType: ApplianceInverterType | "";
  microwaveSmartType: MicrowaveSmartType | "";
};

export function defaultMicrowaveSpecFormState(): MicrowaveSpecFormState {
  return {
    applianceInverterType: "",
    microwaveSmartType: "",
  };
}

export function isMicrowaveSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is MicrowaveSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (MICROWAVE_SUBCATEGORY_IDS as readonly string[]).includes(subcategoryId)
  );
}

export function isCanonicalMicrowaveSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return subcategoryId === CANONICAL_MICROWAVE_SUBCATEGORY_ID;
}

export function getMicrowaveSpecSectionLabel(): string {
  return "Microwave details";
}

/** Locked: Inverter? → Smart? → brand? → Microwave. */
export function composeMicrowaveTitleFromAttributes(
  attrs: ListingSpecAttributes,
  identity?: ListingTitleIdentity | null,
): string {
  const inverter = attrs.appliance_inverter_type;
  const smart = attrs.microwave_smart_type;
  if (!isApplianceInverterType(inverter)) return "";
  if (!isMicrowaveSmartType(smart)) return "";

  return composeListingTitleFromSlotsWithIdentity(
    {
      inverter: inverterTitleToken(inverter),
      smart: smartTitleToken(smart),
      product: "Microwave",
    },
    identity,
  );
}

export function buildMicrowaveAttributesFromForm(
  form: MicrowaveSpecFormState,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = {};
  if (isApplianceInverterType(form.applianceInverterType)) {
    out.appliance_inverter_type = form.applianceInverterType;
  }
  if (isMicrowaveSmartType(form.microwaveSmartType)) {
    out.microwave_smart_type = form.microwaveSmartType;
  }
  return out;
}

export function getMicrowaveSpecFormValidationMessage(
  form: MicrowaveSpecFormState,
): string | undefined {
  const issues = validateStoredMicrowaveAttributes(
    buildMicrowaveAttributesFromForm(form),
  );
  return issues[0]?.message;
}

export type MicrowaveFormHydrateOptions = {
  subcategoryId?: string | null;
  title?: string | null;
};

/** Hydrate sell/edit form from stored `items.attributes` (+ legacy alias fallback). */
export function microwaveFormFromAttributes(
  attributes: unknown,
  options?: MicrowaveFormHydrateOptions,
): MicrowaveSpecFormState {
  const base = defaultMicrowaveSpecFormState();
  const attrs = asListingSpecAttributes(attributes);
  const inverter = attrs.appliance_inverter_type;
  const smart = attrs.microwave_smart_type;
  let applianceInverterType: ApplianceInverterType | "" =
    isApplianceInverterType(inverter) ? inverter : base.applianceInverterType;
  let microwaveSmartType: MicrowaveSmartType | "" = isMicrowaveSmartType(smart)
    ? smart
    : base.microwaveSmartType;
  const legacyMap = getLegacySubcategoryInverterMapping(options?.subcategoryId);
  if (
    legacyMap &&
    legacyMap.legacyId !== legacyMap.canonicalId &&
    options?.subcategoryId
  ) {
    if (!applianceInverterType) {
      applianceInverterType = resolveInverterTypeForLegacySubcategory(
        options.subcategoryId,
        options.title,
      );
    }
    if (!microwaveSmartType) {
      microwaveSmartType = resolveMicrowaveSmartTypeForLegacySubcategory(
        options.subcategoryId,
        options.title,
      );
    }
  }
  return {
    applianceInverterType,
    microwaveSmartType,
  };
}

export function validateStoredMicrowaveAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const attrs = attributes as ListingSpecAttributes;
  const inverter = attrs.appliance_inverter_type;
  const smart = attrs.microwave_smart_type;

  if (inverter === undefined || inverter === null || inverter === "") {
    pushApplianceInverterRequiredIssue(issues);
  } else if (!isApplianceInverterType(inverter)) {
    pushApplianceInverterInvalidIssue(issues);
  }

  if (smart === undefined || smart === null || smart === "") {
    issues.push({
      key: "microwave_smart_type",
      code: "required",
      message: "Choose smart or non-smart.",
    });
  } else if (!isMicrowaveSmartType(smart)) {
    issues.push({
      key: "microwave_smart_type",
      code: "invalid_enum",
      message: "Smart type must be smart or non-smart.",
    });
  }

  return issues;
}

export { APPLIANCE_INVERTER_TYPES, MICROWAVE_SMART_TYPES };
